import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AxiosError } from 'axios';
import './Callback.css';
import ErrorPage from '../pages/ErrorPage';
import { ErrorTranslationKey } from '../translations/errors';
import {
  CredentialOfferRequest,
  ErrorState,
  TokenResponse,
  UserInfo,
} from '../types/callback';
import { getLanguage } from '../utils/language';
import { authService } from '../services/authService';
import UserAlreadyExistsModal from '../pages/UserAlreadyExistsModal';
import { useTranslation } from 'react-i18next';

interface MessageEventData {
  type: string;
  did: string;
  device: string;
}

const HTTP_ERROR_MAP: Record<number, ErrorTranslationKey> = {
  400: 'invalidRequest',
  401: 'authenticationFailed',
  403: 'accessDenied',
};

const getErrorKey = (error: AxiosError): ErrorTranslationKey => {
  if (error.response) {
    return HTTP_ERROR_MAP[error.response.status] || 'generalError';
  }
  return error.request ? 'connectionError' : 'generalError';
};

const Callback: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [tokenResponse, setTokenResponse] = useState<TokenResponse | null>(
    null,
  );
  const [error, setError] = useState<ErrorState | null>(null);
  const [userExists, setUserExists] = useState<boolean>(false);
  const [userDid, setUserDid] = useState<string | undefined>(undefined);
  const [showWaiting, setShowWaiting] = useState<boolean>(false);
  const requestMade = useRef<boolean>(false);
  const access_token = localStorage.getItem('access_token');
  const language = getLanguage();
  const navigate = useNavigate();

  useEffect(() => {
    const handleMessage = async (event: MessageEvent) => {
      try {
        const { type, did, device } = JSON.parse(
          JSON.stringify(event.data),
        ) as MessageEventData;
        if (type === 'IdCATOnboarding completed') {
          const userInfo = JSON.parse(
            localStorage.getItem('oauthTokenResponse') || '{}',
          ) as UserInfo;
          userInfo.dispositiu = device;
          localStorage.setItem('oauthTokenResponse', JSON.stringify(userInfo));
          await authService.notifyIssuance(userInfo, device, did, access_token);
          // window.location.href = PUBLIC_URL + "/onboarding-finish";
          navigate('/onboarding-finish');
        }
      } catch (err) {
        console.error('Error processing message:', err);
        setError({
          title: t('errors.messageProcessing.title'),
          message: t('errors.messageProcessing.message'),
        });
        setShowWaiting(false);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [language, access_token]);

  useEffect(() => {
    const fetchToken = async (code: string) => {
      try {
        setShowWaiting(true);
        const responseData = await authService.fetchToken(code, access_token);
        setTokenResponse(responseData);
        localStorage.setItem(
          'oauthTokenResponse',
          JSON.stringify(responseData),
        );

        const walletUser = await authService.getWalletUser(
          responseData.document,
          access_token,
        );
        if (walletUser && walletUser.codi === '0') {
          console.log('wallet user' + JSON.stringify(walletUser));
          setUserExists(true);
          setUserDid(walletUser.did);
          return;
        }
        if (walletUser && walletUser.codi !== '1') {
          setError({
            title: t('errors.aocWalletUserError.title'),
            message: t('errors.aocWalletUserError.message'),
          });
          setShowWaiting(false);
          return;
        }

        await triggerCredentialOffer(responseData);
      } catch (error) {
        const axiosError = error as AxiosError;
        const errorKey = getErrorKey(axiosError);
        setError({
          title: t(`errors.${errorKey}.title`),
          message: t(`errors.${errorKey}.message`),
        });
        setShowWaiting(false);
      }
    };

    const code = searchParams.get('code');
    if (code && !requestMade.current) {
      requestMade.current = true;
      void fetchToken(code).catch((error) => {
        console.error('Unhandled error in fetchToken:', error);
        setError({
          title: t('errors.generalError.title'),
          message: t('errors.generalError.message'),
        });
        setShowWaiting(false);
      });
    }
  }, [searchParams, language, access_token]);

  const triggerCredentialOffer = async (tokenResponse: TokenResponse) => {
    // Replace undefined expiration_days with the response's data when available
    const credentialOfferRequest: CredentialOfferRequest = {
      issuanceId: tokenResponse.issuance_id,
      document_number: tokenResponse.document,
      document_type: tokenResponse.document_tipus,
      family_name: tokenResponse.cognoms,
      given_name: tokenResponse.nom,
      surname1: tokenResponse.cognom_primer,
      surname2: tokenResponse.cognom_segon,
      email_address: tokenResponse.email,
      nationality: tokenResponse.pais_codi,
      assurance_level: tokenResponse.assuranceLevel,
      expiry_date: tokenResponse.data_caducitat,
      expiration_days: undefined,
    };
    if (userDid) {
      try {
        await authService.revokeCredentials(userDid, access_token);
        setUserDid(undefined);
      } catch (error) {
        setUserDid(undefined);
        setUserExists(true);
        setUserExists(false);
        setShowWaiting(false);
        setError({
          title: t('errors.generalError.title'),
          message: t('errors.generalError.message'),
        });
        return;
      }
    }
    const deepLink = await authService.processCredentialOffer(
      credentialOfferRequest,
      access_token,
    );
    if ((window as any).ReactNativeWebView) {
      (window as any).ReactNativeWebView.postMessage(deepLink);
    } else {
      console.log('Not running in React Native WebView');
    }
    setError(null);
  };

  if (error) {
    return (
      <ErrorPage
        title={error.title}
        message={error.message}
        onClose={() => {
          navigate('/');
        }}
      />
    );
  }

  if (userExists) {
    return (
      <UserAlreadyExistsModal
        onClose={() => {
          navigate('/');
        }}
        onAccept={async () => {
          if (tokenResponse) {
            setUserExists(false);
            await triggerCredentialOffer(tokenResponse);
          } else {
            setUserExists(false);
            setError({
              title: t('errors.generalError.title'),
              message: t('errors.generalError.message'),
            });
            setShowWaiting(false);
            return;
          }
        }}
      />
    );
  }

  return (
    <div className="callback-container">
      <div className="callback-content">
        {showWaiting && (
          <div className="waiting-screen-container">
            <div className="waiting-logo-block">
              <div className="idcat-logo">
                <img
                  src="/images/logo-idCAT.png"
                  alt="idCAT Logo"
                  className="loading-logo-idcat"
                />
              </div>
              <div className="waiting-spinner-block">
                <img
                  src="/images/loading.png"
                  alt="idCAT Loading"
                  className="loading-spinner"
                />
                <span className="waiting-text">{t('global.loading')}</span>
              </div>
            </div>
            <div className="aoc-footer">
              <div className="aoc-logo">
                <img src="/images/AOC-banner.png" alt="AOC banner" />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Callback;
